import { buildDrugQueryVariants, searchDrugs, DrugMapping } from '../lib/drugMappings';

export type DrugSource =
  | 'local'
  | 'extended'
  | 'extended_popular'
  | 'identity'
  | 'cn_to_inn'
  | 'cn_alias_to_inn'
  | 'extended_cn_to_inn'
  | 'extended_cn_alias_to_inn'
  | 'embedded_latin'
  | 'brand'
  | 'cn_combination'
  | 'normalize_drug_name'
  | 'normalize_drug_query'
  | 'inn'
  | 'inn_brand'
  | 'inn_brand_contains'
  | 'inn_contains'
  | 'inn_prefix'
  | 'inn_variant'
  | 'matcher'
  | 'fda'
  | 'ema'
  | 'pmda'
  | 'external'
  | 'manual';

export interface ExtendedDrugResult {
  id: string;
  nameZh: string;
  nameEn: string;
  inn?: string;
  category?: string;
  therapeuticClass?: string;
  aliasesZh?: string[];
  aliasesEn?: string[];
  components?: string[];
  componentsCn?: string[];
  queryTermsEn?: string[];
  queryTermsCn?: string[];
  isCombination?: boolean;
  warnings?: string[];
  source: DrugSource;
  confidence: number;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(item => String(item ?? '').trim())
    .filter(Boolean);
}

function decodeLegacyEntities(value: string): string {
  return value
    .replace(/&#x27;|&#39;|&apos;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/&amp;/gi, '&');
}

function parseNameParts(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(String).map(item => item.trim()).filter(Boolean);
  }

  const text = decodeLegacyEntities(String(value ?? '')).trim();
  if (!text.startsWith('[') || !text.endsWith(']')) {
    return [];
  }

  try {
    const parsed = JSON.parse(text.replace(/'/g, '"'));
    return Array.isArray(parsed)
      ? parsed.map(String).map(item => item.trim()).filter(Boolean)
      : [];
  } catch {
    return [];
  }
}

export function normalizeEnglishDrugField(value: unknown): string {
  const parts = parseNameParts(value);
  if (parts.length > 0) {
    return parts.map(part => part.toLowerCase().replace(/\s+/g, '_')).join('_');
  }
  return decodeLegacyEntities(String(value ?? '')).trim();
}

export function normalizeChineseDrugField(value: unknown): string {
  const parts = parseNameParts(value);
  if (parts.length > 0) {
    return parts.join('');
  }
  return decodeLegacyEntities(String(value ?? '')).trim();
}

function hasLatinText(value: unknown): boolean {
  return /[A-Za-z]/.test(String(value ?? ''));
}

function preferredEnglishDrugField(...values: unknown[]): unknown {
  return values.find(value => hasLatinText(normalizeEnglishDrugField(value))) ?? values.find(Boolean) ?? '';
}

function extractEmbeddedLatinName(value: string): string {
  const matches = value.match(/[A-Za-z][A-Za-z0-9_./+\- ]*[A-Za-z0-9]/g) || [];
  const best = matches
    .map(item => item.trim().replace(/\s+/g, ' '))
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)[0];
  return best || '';
}

function extractChineseName(value: string): string {
  const latin = extractEmbeddedLatinName(value);
  const withoutLatin = latin ? value.replace(latin, '') : value;
  return withoutLatin
    .replace(/[()（）]/g, '')
    .replace(/\s+/g, '')
    .trim();
}

function preferredManualDrugName(query: string): string {
  const variants = buildDrugQueryVariants(query);
  const raw = query.trim();
  return (
    variants.find(item => item !== raw && /[\u4e00-\u9fff]/.test(item) && item.length >= 2) ||
    variants.find(item => item !== raw && item.length >= 2) ||
    variants.find(item => /[\u4e00-\u9fff]/.test(item) && item.length >= 2) ||
    variants.find(item => item.length >= 2) ||
    raw
  );
}

export function createManualDrugResult(query: string): ExtendedDrugResult | null {
  const name = preferredManualDrugName(query);
  if (name.trim().length < 2) return null;
  const embeddedEnglish = extractEmbeddedLatinName(query);
  const embeddedChinese = embeddedEnglish ? extractChineseName(query) : '';
  const nameZh = embeddedEnglish ? (embeddedChinese || name) : name;
  const nameEn = embeddedEnglish || name;
  const isCombination = /[+/]|(?:\band\b)|(?:_and_)/i.test(nameEn) || /复方|二甲双胍|缬沙坦/.test(nameZh);

  return {
    id: `manual_${nameEn.replace(/[^\w\u4e00-\u9fff]+/g, '_')}`,
    nameZh,
    nameEn,
    inn: nameEn,
    category: '自定义输入',
    therapeuticClass: '按当前药名实时查询',
    aliasesZh: [nameZh],
    aliasesEn: embeddedEnglish ? [embeddedEnglish] : [],
    components: embeddedEnglish ? [embeddedEnglish] : [nameEn],
    componentsCn: [nameZh],
    queryTermsEn: embeddedEnglish ? [embeddedEnglish, nameEn] : [nameEn],
    queryTermsCn: [nameZh],
    isCombination,
    warnings: ['未从标准药品库确认，后端将按当前输入实时识别。'],
    source: 'manual',
    confidence: embeddedEnglish ? 0.45 : 0.35,
  };
}

function normalizeResultKey(value: unknown): string {
  return normalizeEnglishDrugField(value)
    .toLowerCase()
    .replace(/[\s_]+/g, '_')
    .replace(/[^\w\u4e00-\u9fff]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function collectResultKeys(result: ExtendedDrugResult): Set<string> {
  const values: unknown[] = [
    result.nameZh,
    result.nameEn,
    result.inn,
    ...(result.aliasesZh || []),
    ...(result.aliasesEn || []),
    ...(result.components || []),
    ...(result.componentsCn || []),
    ...(result.queryTermsEn || []),
    ...(result.queryTermsCn || []),
  ];

  return new Set(
    values
      .map(normalizeResultKey)
      .filter(key => key.length >= 2),
  );
}

function isSameSearchCandidate(a: ExtendedDrugResult, b: ExtendedDrugResult): boolean {
  const aKeys = collectResultKeys(a);
  const bKeys = collectResultKeys(b);
  for (const key of aKeys) {
    if (bKeys.has(key)) return true;
  }
  return false;
}

function hasTrustedCatalogResult(results: ExtendedDrugResult[]): boolean {
  return results.some(result =>
    result.source !== 'manual' &&
    result.source !== 'normalize_drug_query' &&
    result.confidence >= 0.7,
  );
}

export class DrugSearchService {
  private abortController: AbortController | null = null;
  private apiBase: string;

  constructor(apiBase: string) {
    this.apiBase = apiBase;
  }

  async searchAPI(query: string): Promise<ExtendedDrugResult[]> {
    let timeoutId: number | null = null;
    try {
      if (this.abortController) {
        this.abortController.abort();
      }

      const controller = new AbortController();
      this.abortController = controller;
      timeoutId = window.setTimeout(() => controller.abort(), 2500);

      const response = await fetch(
        `${this.apiBase}/api/drugs/search?q=${encodeURIComponent(query)}&limit=15&auto_external=false`,
        {
          signal: controller.signal,
          headers: {
            'Accept': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();

      return (data.results || []).map((item: any) => {
        const nameEn = normalizeEnglishDrugField(
          preferredEnglishDrugField(item.inn, item.nameEn, item.name)
        );
        const inn = normalizeEnglishDrugField(
          preferredEnglishDrugField(item.inn, nameEn, item.nameEn)
        );
        return {
          id: item.id || `api_${inn}`,
          nameZh: normalizeChineseDrugField(item.nameZh),
          nameEn,
          inn,
          category: item.category || '',
          therapeuticClass: item.therapeuticClass || '',
          aliasesZh: asStringArray(item.aliasesZh || item.aliases_zh),
          aliasesEn: asStringArray(item.aliasesEn || item.aliases_en),
          components: asStringArray(item.components),
          componentsCn: asStringArray(item.componentsCn || item.components_cn),
          queryTermsEn: asStringArray(item.queryTermsEn || item.query_terms_en),
          queryTermsCn: asStringArray(item.queryTermsCn || item.query_terms_cn),
          isCombination: Boolean(item.isCombination ?? item.is_combination),
          warnings: asStringArray(item.warnings),
          source: item.source || 'inn',
          confidence: item.confidence || 0.8
        };
      });
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        return [];
      }
      console.warn('[DrugSearch] API search failed:', error);
      return [];
    } finally {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      if (this.abortController) {
        this.abortController = null;
      }
    }
  }

  convertToLocalFormat(drug: DrugMapping): ExtendedDrugResult {
    return {
      id: drug.id,
      nameZh: drug.nameZh,
      nameEn: drug.nameEn,
      category: drug.category,
      therapeuticClass: drug.therapeuticClass,
      aliasesZh: drug.aliasesZh,
      aliasesEn: drug.aliasesEn,
      components: [drug.inn || drug.nameEn],
      componentsCn: [drug.nameZh],
      queryTermsEn: [drug.inn || drug.nameEn, drug.nameEn, ...drug.aliasesEn],
      queryTermsCn: [drug.nameZh, ...drug.aliasesZh],
      isCombination: /[+/]|(?:\band\b)|(?:_and_)/i.test(drug.inn || drug.nameEn),
      warnings: [],
      source: 'local',
      confidence: 1.0
    };
  }

  mergeAndDeduplicateResults(
    localResults: DrugMapping[],
    apiResults: ExtendedDrugResult[]
  ): ExtendedDrugResult[] {
    const merged: ExtendedDrugResult[] = [];
    const seenIds = new Set<string>();

    localResults.forEach(drug => {
      const converted = this.convertToLocalFormat(drug);
      if (!seenIds.has(converted.nameEn.toLowerCase())) {
        seenIds.add(converted.nameEn.toLowerCase());
        merged.push(converted);
      }
    });

    apiResults.forEach(result => {
      const key = (result.inn || result.nameEn).toLowerCase();
      if (!seenIds.has(key)) {
        seenIds.add(key);
        merged.push(result);
      }
    });

    merged.sort((a, b) => {
      if (a.source === 'local' && b.source !== 'local') return -1;
      if (b.source === 'local' && a.source !== 'local') return 1;
      return b.confidence - a.confidence;
    });

    return merged;
  }

  async search(query: string): Promise<{
    results: ExtendedDrugResult[];
    sources: string[];
  }> {
    const localResults = searchDrugs(query, 8);
    const apiResults = await this.searchAPI(query);
    const merged = this.mergeAndDeduplicateResults(localResults, apiResults);
    const manualResult = createManualDrugResult(query);
    const finalResults = [...merged];

    if (manualResult) {
      const duplicateIndex = finalResults.findIndex(result =>
        isSameSearchCandidate(result, manualResult),
      );
      const trustedCatalogHit = hasTrustedCatalogResult(finalResults);

      if (duplicateIndex === -1) {
        if (trustedCatalogHit) {
          finalResults.push(manualResult);
        } else {
          finalResults.unshift(manualResult);
        }
      } else if (!trustedCatalogHit && finalResults[duplicateIndex].source !== 'manual') {
        finalResults.splice(duplicateIndex, 0, manualResult);
      }
    }

    const sources = [...new Set([
      ...(localResults.length > 0 ? ['local' as DrugSource] : []),
      ...apiResults.map(r => r.source),
      ...finalResults.map(r => r.source),
    ])];

    return {
      results: finalResults.slice(0, 12),
      sources
    };
  }

  cancel() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }
}
